import { defineStore } from "pinia";
import { ref } from "vue";
import { appContainer } from "../container";
import { UserService } from "../services/userService";
import { useUserStore } from "./UserStore";
import { InvitedUserInterface, Roles } from "../interface/UserInterface";
import { CompanyInterface } from "../interface/CompanyInterface";

/**
 * Caches the companies the logged-in admin manages.
 *
 * The list is derived from the admin-type invitations that the app already
 * fetches on navigation (see the HasInvitation guard) — so populating this
 * store costs no extra API call. The Users listing reuses these company ids
 * to load the admin's participants/raters per company.
 */
export const useAdminCompaniesStore = defineStore("adminCompanies", () => {
  const userService = appContainer.resolve(UserService);

  const companyIds = ref<number[]>([]);
  const companies = ref<CompanyInterface[]>([]);
  const loaded = ref(false);

  /**
   * Derive and cache the admin's companies from an already-fetched list of
   * invitations. No network call — reuses data the guard already has.
   */
  function setFromInvitations(items: InvitedUserInterface[]) {
    const adminInvites = items.filter(
      (item) => item.invitation_type === Roles.Admin && item.company_id,
    );
    companyIds.value = [
      ...new Set(adminInvites.map((item) => item.company_id as number)),
    ];
    companies.value = adminInvites
      .map((item) => item.company)
      .filter(Boolean) as CompanyInterface[];
    loaded.value = true;
  }

  /**
   * Fallback fetch: used when the store was never populated by the guard
   * (e.g. a hard refresh landing directly on the Users page).
   */
  async function loadCompanies(force = false) {
    if (loaded.value && !force) return;
    const userStore = useUserStore();
    const result = await userService.getInvitedUser({
      invited_user: userStore.user?.id,
      invitationType: Roles.Admin,
      invitation_sent: true,
      allUsers: true,
      all: true,
    });
    setFromInvitations(result.items);
  }

  return { companyIds, companies, loaded, setFromInvitations, loadCompanies };
});
